import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPublishedStories, type PublicStory } from "@/lib/publicStories";

export interface PublicStoriesResult {
  stories: PublicStory[];
  featured: PublicStory | null;
  loading: boolean;
  error: string;
  reload: () => void;
}

/**
 * Loads the merged public journal (farmer stories + blog posts) once and derives
 * the featured story plus the remaining grid stories from it.
 */
export function usePublicStories(): PublicStoriesResult {
  const [stories, setStories] = useState<PublicStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setStories(await fetchPublishedStories());
    } catch {
      setError("We couldn't load the journal right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const featured = useMemo(
    () => stories.find((story) => story.featured) || stories[0] || null,
    [stories],
  );

  return { stories, featured, loading, error, reload: load };
}